"use client";

import Link from "next/link";
import { useEffect, useState } from "react";
import { useLocale, useTranslations } from "next-intl";
import { AlertTriangle, Copy } from "lucide-react";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import {
  crashDetailsText,
  crashKindOf,
  crashReference,
  pageShowsTranslation,
  sendCrashReport,
  type CrashKind,
} from "@/lib/crash-report";

/**
 * The error screen for everything under `app/[locale]` - `EX-SFX03`,
 * ADR-0108, the canvas's "Error page" board.
 *
 * It says which of two things happened. A page a translator or an extension
 * changed under React ("Your browser changed this page") is fixed by a reload
 * and by nothing else: the broken nodes sit outside this boundary, which is why
 * "Try again" failed eight times in a row in the setter's console, so Reload
 * is the first action and there is no Try again. Anything else is ours, said
 * so, with the reference that finds its report.
 *
 * The message is shown in production too: it is what the browser reported
 * (Next replaces a server error's with a generic sentence and a digest, which
 * is shown instead), and a person sending it to us is the point.
 */
export default function LocaleRouteError({ error }: { error: Error & { digest?: string }; reset: () => void }) {
  const t = useTranslations("errorPage");
  const locale = useLocale();
  const [report] = useState(() => {
    const translated = pageShowsTranslation();
    return { reference: crashReference(), translated, kind: crashKindOf(error, translated) as CrashKind };
  });
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    console.error("[locale] route error", {
      reference: report.reference,
      digest: error.digest,
      message: error.message,
      stack: error.stack,
    });
    sendCrashReport({
      reference: report.reference,
      kind: report.kind,
      surface: "page",
      error: { name: error.name, message: error.message, ...(error.digest ? { digest: error.digest } : {}) },
      locale,
      translated: report.translated,
    });
  }, [error, locale, report]);

  const browser = report.kind === "browser_changed_page";
  const message = [error.name && error.name !== "Error" ? `${error.name}: ` : "", error.message || ""].join("");

  const copy = async () => {
    const text = crashDetailsText({
      reference: report.reference,
      error: { name: error.name, message: error.message, ...(error.digest ? { digest: error.digest } : {}) },
      path: typeof window === "undefined" ? "/" : window.location.pathname,
      at: new Date(),
      userAgent: typeof navigator === "undefined" ? "" : navigator.userAgent,
    });
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };

  return (
    <div className="mx-auto flex min-h-[60vh] w-full max-w-xl flex-col justify-center px-4 py-12 sm:px-6">
      <div className="rounded-2xl border border-[rgb(var(--border))] bg-white p-6 shadow-card sm:p-8">
        <span
          aria-hidden
          className={cn(
            "flex h-11 w-11 items-center justify-center rounded-xl",
            browser ? "bg-amber-50 text-amber-700" : "bg-red-50 text-red-700",
          )}
        >
          <AlertTriangle className="h-5 w-5" />
        </span>
        <h1 className="mt-4 text-xl font-semibold text-neutral-900">
          {browser ? t("browserTitle") : t("codeTitle")}
        </h1>
        <p className="mt-2 text-sm leading-relaxed text-[rgb(var(--muted-foreground))]">
          {browser ? t("browserBody") : t("codeBody")}
        </p>
        <p className="mt-3 text-sm leading-relaxed text-[rgb(var(--muted-foreground))]">{t("savedNote")}</p>

        <div className="mt-6 flex flex-wrap gap-3">
          <Button type="button" className="h-11" onClick={() => window.location.reload()}>
            {t("reload")}
          </Button>
          <Button asChild variant="outline" className="h-11">
            <Link href={`/${locale}/dashboard`}>{t("dashboard")}</Link>
          </Button>
        </div>

        <div className="mt-6 rounded-xl border border-[rgb(var(--border))] bg-neutral-50 p-4">
          <p className="text-xs font-semibold text-neutral-700">{t("reportedTitle")}</p>
          <p className="mt-1.5 break-words font-mono text-xs leading-relaxed text-neutral-800">
            {message || t("noMessage")}
          </p>
          {error.digest ? (
            <p className="mt-1 font-mono text-[11px] text-[rgb(var(--muted-foreground))]">
              {t("digest", { digest: error.digest })}
            </p>
          ) : null}
          <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
            <p className="text-xs text-[rgb(var(--muted-foreground))]">
              {t("reference", { reference: report.reference })}
            </p>
            <Button type="button" variant="outline" size="sm" className="h-11 sm:h-9" onClick={() => void copy()}>
              <Copy aria-hidden className="h-3.5 w-3.5" />
              <span>{copied ? t("copied") : t("copy")}</span>
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
